import { EventEmitter, Inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Environment, ENVIRONMENT } from '@netfoundry-ui/shared/model';
import { catchError, delay } from 'rxjs/operators';
import {BehaviorSubject, of as observableOf} from 'rxjs';
import { throwError as observableThrowError } from 'rxjs/internal/observable/throwError';

import {cloneDeep, get, isEmpty, isNumber, isNil}from 'lodash';
import moment from 'moment';

import { HTTP_CLIENT, TokenService } from './token.service';
import { LoggerService } from './logger.service';
import { INFRASTRUCTURE_URLS, ZITI_BETA_LIST_ROUTES, ZITI_URLS } from './urls';
import { ApiService } from './api.service';
import { IdentityPreferencesService } from './identity-preferences.service';
import { OrganizationPreferencesService } from './organization-preferences.service';
import { NetworkVersionService } from './network-version.service';

const WHATS_NEW_ITERATION_KEY = 'nf_whats_new_iteration';

@Injectable({ providedIn: 'root' })
export class FeatureService {
    public experimentalFeatures = false;
    public isCloudZiti = false;
    /** Beta features are support-only. Set by AuthorizationService once grants resolve; gates
     * betaFeaturesEnabled so the flag stays off (and unsettable) for everyone else. */
    public supportUser = false;

    user;
    apiUrl;
    userPreferences;
    orgPreferences;
    orgPreferencesInit = false;
    organization;
    currentOrganization;
    tenant;
    preferenceId;
    experimentalFeaturesChanged = new EventEmitter();
    /** Fires with the new value when betaFeaturesEnabled flips, once the preference has actually landed. */
    betaFeaturesChanged = new EventEmitter<boolean>();
    disableNewNavChanged = new EventEmitter();

    minCloudZitiVersion = 8;
    minCloudZitiMinorVersion = 0;
    minCloudZitiPatchVersion = 0;

    public darkModeSetting: BehaviorSubject<string> = new BehaviorSubject<string>('');

    constructor(
        private logger: LoggerService,
        private apiService: ApiService,
        private tokenService: TokenService,
        private identityPreferencesService: IdentityPreferencesService,
        private organizationPreferencesService: OrganizationPreferencesService,
        private networkVersionService: NetworkVersionService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.isCloudZiti = environment.isCloudZiti;
        this.apiUrl = get(environment, 'identityConfig.url');
        this.apiService.currentOrg.subscribe((org) => {
            this.organization = org;
            this.checkFeatures(org.getId());
        });
        this.apiService.currentOrganization.subscribe((org) => {
          this.currentOrganization = org;
        });
        this.apiService.currentUser.subscribe((user) => {
            this.user = user;
            this.experimentalFeaturesChanged.emit(this.experimentalFeatures);
        });

        this.apiService.currentUser.subscribe((user) => {
            if (isEmpty(user)) {
                return;
            }
            this.user = user;
        });

        this.apiService.currentTenant.subscribe((tenant) => {
            this.tenant = tenant;
        });
        this.apiService.currentUserPreferences.subscribe((userPreferences) => {
            const wasBeta = this.betaFeaturesEnabled;
            this.userPreferences = cloneDeep(userPreferences?.preferences);
            if (this.betaFeaturesEnabled !== wasBeta) {
                this.betaFeaturesChanged.emit(this.betaFeaturesEnabled);
            }
        });
        this.apiService.currentOrgPreferences.subscribe((orgPreferences) => {
            this.orgPreferences = cloneDeep(orgPreferences);
            this.orgPreferencesInit = true;
        });
    }

    _whatsNewIteration = 0;

    public get whatsNewIteration() {
        if (!this.user) {
            return 0;
        }
        const storageKey = `whatsNewIteration_${this.user?.id}`;
        const prefKey = 'whatsNewIteration';
        const localIteration = localStorage.getItem(storageKey);
        const localIterationNumber = Number.parseInt(localIteration, 10);
        const prefSetting = get(this.userPreferences, prefKey);
        if (!isEmpty(prefSetting) || prefSetting === 0) {
            return prefSetting;
        } else if (isNumber(localIterationNumber)) {
            return localIterationNumber;
        } else {
            return 0;
        }
    }

    public set whatsNewIteration(iteration) {
        this._whatsNewIteration = iteration;
        const prefKey = `whatsNewIteration`;
        const storageKey = `whatsNewIteration_${this.user?.id}`;
        localStorage.setItem(storageKey, `${iteration}`);
        this.identityPreferencesService.setPreference(prefKey, this._whatsNewIteration);
    }

    _powerUserEnabled;

    public get powerUserEnabled() {
        if (!this.user) {
            return false;
        }

        const localStorageSetting = localStorage.getItem('powerUserEnabled_' + this.user.id) === 'on';
        if (this.userPreferences?.powerUserEnabled === false) {
            return false;
        } else {
            return localStorageSetting || this.userPreferences?.powerUserEnabled;
        }
    }

    _enableNewNavigation = false;

    public get enableNewNavigation() {
        if (!this.user) {
            return false;
        }
        const storageKey = `enableNewNavigation_${this.user?.id}`;
        const prefKey = 'enableNewNavigation';
        const localStorageSetting = localStorage.getItem(storageKey) === 'true';
        const prefSetting = get(this.userPreferences, prefKey);
        if (prefSetting === false) {
            return false;
        } else {
            return localStorageSetting || prefSetting;
        }
    }

    public set enableNewNavigation(enabled) {
        this._enableNewNavigation = enabled;
        const enableStorageKey = `enableNewNavigation_${this.user?.id}`;
        const enablePrefKey = 'enableNewNavigation';
        const disableStorageKey = `newNavigationDisabled_${this.user?.id}`;
        const disablePrefKey = 'newNavigationDisabled';
        if (this._enableNewNavigation) {
            localStorage.setItem(enableStorageKey, 'true');
            localStorage.setItem(disableStorageKey, 'false');
        } else {
            localStorage.setItem(enableStorageKey, 'false');
            localStorage.setItem(disableStorageKey, 'true');
        }
        const keyValuePairs = [
            {
                key: enablePrefKey,
                value: enabled,
            },
            {
                key: disablePrefKey,
                value: !enabled,
            },
        ];
        this.identityPreferencesService.setPreferences(keyValuePairs);
    }

    _disableNewNavigation;

    public get disableNewNavigation() {
        if (!this.enableNewNavigation) {
            return true;
        }
        if (!this.user) {
            return false;
        }

        const storageKey = `newNavigationDisabled_${this.user?.id}`;
        const prefKey = 'newNavigationDisabled';
        const localStorageSetting = localStorage.getItem(storageKey) === 'true';
        const prefSetting = get(this.userPreferences, prefKey);
        if (prefSetting === false) {
            return false;
        } else {
            return localStorageSetting || prefSetting;
        }
    }

    public set disableNewNavigation(disabled) {
        this._disableNewNavigation = disabled;
        const prefKey = `newNavigationDisabled`;
        const storageKey = `newNavigationDisabled_${this.user?.id}`;
        if (this._disableNewNavigation) {
            localStorage.setItem(storageKey, 'true');
        } else {
            localStorage.setItem(storageKey, 'false');
        }
        this.identityPreferencesService.setPreference(prefKey, this._disableNewNavigation);
        this.disableNewNavChanged.emit(disabled);
    }

    public get showManagedEntities() {
      if (!this.user) {
        return false;
      }

      const prefKey = 'showManagedEntities';
      const prefSetting = get(this.userPreferences, prefKey);
      return prefSetting;
    }

    public set showManagedEntities(disabled) {
      const prefKey = `showManagedEntities`;
      this.identityPreferencesService.setPreference(prefKey, disabled);
    }

    // Per-user opt-in for in-progress features (currently the redesigned dashboard). Mirrors the
    // Managed Entities pref: backed by the schemaless identity-preferences bag, off when unset.
    public get betaFeaturesEnabled() {
      // Support-only: off for everyone else regardless of any stored preference.
      if (!this.user || !this.supportUser) {
        return false;
      }
      return get(this.userPreferences, 'betaFeaturesEnabled');
    }

    public set betaFeaturesEnabled(enabled) {
      if (!this.supportUser) {
        return;
      }
      // Apply it now: the saved preference comes back later, and a redirect guard that reads the flag right after the
      // toggle (the app switcher goes to the counterpart page) would still see the old value and send the user back.
      this.userPreferences = { ...(this.userPreferences || {}), betaFeaturesEnabled: enabled };
      this.identityPreferencesService.setPreference('betaFeaturesEnabled', enabled);
    }

    // The network dashboard route, following the beta toggle: the redesigned Overview when beta is on,
    // the legacy Dashboard otherwise. Used by the sidebar item and the app's default landing.
    public get dashboardRoute() {
      return this.betaFeaturesEnabled ? INFRASTRUCTURE_URLS.OVERVIEW : INFRASTRUCTURE_URLS.DASHBOARD;
    }

    // Redesigned identities list when beta is on, legacy list otherwise (mirrors dashboardRoute).
    public get identitiesRoute() {
      return this.betaFeaturesEnabled ? ZITI_URLS.ZITI_IDENTITIES_LIST : ZITI_URLS.ZITI_IDENTITIES;
    }

    // The Ziti overlay connection setting is offered only with beta features on.
    public get zitiOverlayAvailable() {
      return !!this.betaFeaturesEnabled;
    }

    // Per-user opt-in to send controller API calls over the browser WASM Ziti overlay. Off when unset.
    public get zitiOverlayEnabled() {
      return this.zitiOverlayAvailable && !!get(this.userPreferences, 'zitiOverlayEnabled');
    }

    public set zitiOverlayEnabled(enabled) {
      if (!this.zitiOverlayAvailable) {
        return;
      }
      this.identityPreferencesService.setPreference('zitiOverlayEnabled', enabled);
    }

    // Redesigned list URL when beta is on and one exists for this legacy URL, the legacy URL otherwise.
    // Only cloud-ziti has the beta lists; nfconsole keeps the old pages.
    public betaListRoute(legacyRoute: string): string {
      return (this.isCloudZiti && this.betaFeaturesEnabled && ZITI_BETA_LIST_ROUTES[legacyRoute]) || legacyRoute;
    }

    _showGettingStarted;

    public get showGettingStarted() {
        const localStorageSetting = localStorage.getItem(`showGettingStarted_${this.organization?.name}`) === 'true';
        if (this.userPreferences?.showGettingStarted === false) {
            return false;
        } else {
            return localStorageSetting || this.userPreferences?.showGettingStarted;
        }
    }

    public set showGettingStarted(hide) {
        this._showGettingStarted = hide;
        if (this._showGettingStarted) {
            localStorage.setItem(`showGettingStarted_${this.organization?.name}`, 'true');
        } else {
            localStorage.setItem(`showGettingStarted_${this.organization?.name}`, 'false');
        }
        this.identityPreferencesService.setPreference('showGettingStarted', this._showGettingStarted);
    }

    _enableDeleteProtection;

    public get enableDeleteProtection() {
        if (!this.user) {
            return false;
        }
        const tenantId = localStorage.getItem('tenantId');
        const localStorageSetting = localStorage.getItem('enableDeleteProtection_' + tenantId) === 'on';
        if (this.orgPreferences?.enableDeleteProtection === false) {
            return false;
        } else {
            return localStorageSetting || this.orgPreferences?.enableDeleteProtection;
        }
    }

    public set enableDeleteProtection(val) {
      if (!this.orgPreferences) {
        this.orgPreferences = {};
      }
      this.orgPreferences.enableDeleteProtection = val;
      localStorage.setItem('enableDeleteProtection_' + this.tenant.id, 'on');
    }

    public get darkModeEnabled() {
        const localSetting = localStorage.getItem('darkMode_' + this.tenant?.label);
        const identityPrefSetting = this.identityPreferencesService.getPreference('darkModeEnabled');
        if ((this.isCloudZiti || isEmpty(localSetting)) && isNil(identityPrefSetting)) {
          return this.environment.defaultTheme === 'dark';
        }

        const isEnabledLocal = localSetting === 'true';
        if (!this.tenant && !this.environment.realmsSupported) {
            if (!isNil(identityPrefSetting)) {
              return identityPrefSetting;
            } else {
              return isEnabledLocal;
            }
        }

        if ((!this.isCloudZiti && !this.environment.realmsSupported) && isEnabledLocal) {
            return true;
        } else if (identityPrefSetting === 'true' || identityPrefSetting === true) {
            return true;
        } else if (this.isCloudZiti && identityPrefSetting === 'auto') {
            const systemDarkmodeEnabled = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
            return systemDarkmodeEnabled;
        }
        return false;
    }

    public set darkModeEnabled(val: any) {
        localStorage.setItem('darkMode_' + this.tenant?.label, val.toString());
        this.darkModeSetting.next(val);
        if (!this.tenant && !this.currentOrganization) {
            return;
        }
        this.identityPreferencesService.setPreference('darkModeEnabled', val);
    }

    get darkModeUnset() {
      const identityPrefSetting = this.identityPreferencesService.getPreference('darkModeEnabled');
      return !['true', 'false', 'auto'].includes(identityPrefSetting);
    }

    public isNewSelfServiceWorkflow(tenantLabel?) {
        tenantLabel = tenantLabel || this.organization?.name;
        const newSelfService = localStorage.getItem(`nf_new_self_service_${tenantLabel}`) === 'true';
        const showGettingStarted = localStorage.getItem(`showGettingStarted_${tenantLabel}`) === 'true';
        const showSurvey = localStorage.getItem(`showSurvey_${tenantLabel}`) === 'true';
        const isTeams = localStorage.getItem(`isSelfServiceTeams_${tenantLabel}`) === 'true';
        return newSelfService && showGettingStarted && showSurvey && isTeams;
    }

    public get freemiumBillingEnabled() {
        if (!this.user) {
            return false;
        }
        return localStorage.getItem('freemiumBillingEnabled' + this.user.id) === 'on' || true;
    }

    // Check for the feature toggle
    public checkFeatures(orgId) {
        this.logger.info('Check features');

        if (this.environment.production && this.environment.domain !== 'staging') {
            this.experimentalFeatures = false;
            this.experimentalFeaturesChanged.emit(this.experimentalFeatures);
            this.logger.info('Prod environment, disable experimental features');
            return;
        }

        if (orgId === null) {
            this.logger.info('No organization set, skip feature check');
            return;
        }

        // check for experimental features
        if (localStorage.getItem('experimentalFeatures_' + orgId) === 'on') {
            this.logger.info('Experimental features are enabled');
            this.experimentalFeatures = true;
        } else {
            this.logger.info('Experimental features are disabled');
            this.experimentalFeatures = false;
        }
        this.experimentalFeaturesChanged.emit(this.experimentalFeatures);
    }

    // Don't even give the option for hidden features in prod environments
    public allowHiddenFeatures() {
        return this.environment.production === false || this.environment.domain === 'staging';
    }

    /**
     * Same answer as `experimentalFeatures`, but without the timing dependency. `checkFeatures`
     * only runs once the org resolves, which on a cold reload happens AFTER route guards fire —
     * a guard reading the field alone would bounce a legitimate deep link. This recomputes the
     * flag straight from the same inputs checkFeatures uses, so it is correct at any point.
     */
    public isExperimentalEnabled(): boolean {
        if (this.experimentalFeatures) {
            return true;
        }
        if (!this.allowHiddenFeatures()) {
            return false;
        }
        const orgId = localStorage.getItem('organizationId');
        return localStorage.getItem('experimentalFeatures_' + orgId) === 'on';
    }

    public setExperimentalFeatures(flag) {
        if (this.allowHiddenFeatures() === false) {
            this.experimentalFeatures = false;
            this.experimentalFeaturesChanged.emit(this.experimentalFeatures);
            return;
        }

        const orgId = localStorage.getItem('organizationId');
        if (flag) {
            localStorage.setItem('experimentalFeatures_' + orgId, 'on');
            this.experimentalFeatures = true;
        } else {
            localStorage.setItem('experimentalFeatures_' + orgId, 'off');
            this.experimentalFeatures = false;
        }
        this.experimentalFeaturesChanged.emit(this.experimentalFeatures);
    }

    public setEnableDeleteProtection(flag) {
        if (flag) {
            localStorage.setItem('enableDeleteProtection_' + this.tenant.id, 'on');
            this._enableDeleteProtection = true;
        } else {
            localStorage.setItem('enableDeleteProtection_' + this.tenant.id, 'off');
            this._enableDeleteProtection = false;
        }
        return this.organizationPreferencesService.setOrganizationPreference(
            'enableDeleteProtection',
            this._enableDeleteProtection
        );
    }
    _loadingIndicatorFile = '/assets/svgs/neutral-loading-indicator.svg';
    public setLoadingIndicatorFile(indicatorPath) {
      this._loadingIndicatorFile = indicatorPath;
      return this.organizationPreferencesService.setOrganizationPreference(
        'loadingIndicatorFile',
        this._loadingIndicatorFile
      );
    }

    public get loadingIndicatorFile() {
      if (!this.user) {
        return this._loadingIndicatorFile;
      }

      if (this.orgPreferences?.loadingIndicatorFile) {
        return this.orgPreferences?.loadingIndicatorFile;
      } else {
        return this._loadingIndicatorFile;
      }
    }

    public setPowerUserEnabled(flag) {
        if (flag) {
            localStorage.setItem('powerUserEnabled_' + this.user.id, 'on');
            this._powerUserEnabled = true;
        } else {
            localStorage.setItem('powerUserEnabled_' + this.user.id, 'off');
            this._powerUserEnabled = false;
        }
        return this.identityPreferencesService.setPreference('powerUserEnabled', this._powerUserEnabled);
    }

    public whiteLabelSupported(network) {
        const majorVersionNumber = this.apiService.getNetworkVersion(network);
        const minorVersionNumber = this.apiService.getNetworkMinorVersion(network);
        const patchVersionNumber = this.apiService.getNetworkPatchVersion(network);
        const isLegacyZitiWithoutWhitelabel =
            majorVersionNumber === 7 &&
            (minorVersionNumber < 3 || (minorVersionNumber === 3 && patchVersionNumber < 86));
        return !isLegacyZitiWithoutWhitelabel;
    }

    public getShowInfoBubble(type) {
        const localStorageSetting = localStorage.getItem(`show_${type}_info_${this.organization?.name}`) === 'true';
        return localStorageSetting || this.userPreferences?.showGettingStarted;
    }

    public setShowInfoBubble(type, show) {
        if (show) {
            localStorage.setItem(`show_${type}_info_${this.organization?.name}`, 'true');
        } else {
            localStorage.setItem(`show_${type}_info_${this.organization?.name}`, 'false');
        }
    }

    public isSelfServiceTeamsTier(subscription) {
        const cloudRegions = get(subscription, 'subscriptionItems.cloudRegionsQuantity', 0);
        const cloudProviders = get(subscription, 'subscriptionItems.cloudProvidersQuantity', 0);
        return cloudRegions === '1' && cloudProviders === '1';
    }

    public isSelfServiceGrowthTier(subscription) {
        const cloudRegions = get(subscription, 'subscriptionItems.cloudRegionsQuantity', 0);
        const cloudProviders = get(subscription, 'subscriptionItems.cloudProvidersQuantity', 0);
        return cloudRegions === '2' && cloudProviders === '2';
    }

    public isSelfServicePAYG(subscription) {
        const paygSupport = get(subscription, 'subscriptionItems.freemiumPAYGSupport', '');
        return paygSupport === 'true' || paygSupport === true;
    }

    public isSelfServiceAccount(subscription) {
        const isFreemiumPAYG = this.isSelfServicePAYG(subscription);
        const isSelfServiceTeams = this.isSelfServiceTeamsTier(subscription);
        const isSelfServiceGrowth = this.isSelfServiceGrowthTier(subscription);
        return isFreemiumPAYG || isSelfServiceTeams || isSelfServiceGrowth;
    }

    public isCancelledAccount(subscription) {
        if (isEmpty(subscription?.cancelledAt)) {
            return false;
        }
        return moment(subscription.cancelledAt).isBefore(Date.now());
    }

    public isCloudZitiNetwork(network) {
        if (isEmpty(network?.productVersion)) {
            return false;
        }
        const networkVersion = this.networkVersionService.getNetworkVersion(network);
        const minorVersion = this.networkVersionService.getNetworkMinorVersion(network);
        const patchVersion = this.networkVersionService.getNetworkPatchVersion(network);
        return (
            (networkVersion >= this.minCloudZitiVersion &&
                minorVersion >= this.minCloudZitiMinorVersion &&
                patchVersion >= this.minCloudZitiPatchVersion) ||
            network.name === 'v8manualtest'
        );
    }

    public canDisabledServiceEncryption(network) {
        const networkVersion = this.networkVersionService.getNetworkVersion(network);
        const minorVersion = this.networkVersionService.getNetworkMinorVersion(network);
        const patchVersion = this.networkVersionService.getNetworkPatchVersion(network);
        // Disabling service encryption requires Ziti >= 7.3.91 on the legacy 7.x
        // track; the Ziti 2.x successor track always supports it.
        const isLegacyZitiWithoutDisableEncryption =
            networkVersion === 7 &&
            (minorVersion < 3 || (minorVersion === 3 && patchVersion < 91));
        return !isLegacyZitiWithoutDisableEncryption;
    }
    /**
     * Get item by ID
     * Allowed Types:
     *    identity-permission
     *    user-identities
     *    invitations
     *    tenants
     *    operations
     */
    public get(type: string, id: string) {
        const fullpath = `${this.apiUrl}${type}/${id}`;

        // getting the headers. This now checks to determine if the user's token expired
        const headers = this.setHeaders();

        // headers will be not null if the token is valid. If this is the case, kick off the request
        if (headers != null) {
            return this.http
                .get(fullpath, { headers: headers })
                .pipe(catchError((error) => this.handleError(this, error)));
        } else {
            // delaying the response to allow the console time to trigger the logout and terminate the subscription
            return observableOf([]).pipe(delay(1000));
        }
    }

    /**
     * Generic error handler for bad requests
     */
    protected handleError(scope: any, error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            scope.logger.error('Feature Service error occurred:', error.error);
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            scope.logger.error(`Feature Backend returned code ${error.status}`, error.error);
        }
        scope.lastErrorSource.next(error);
        return observableThrowError(error);
    }

    /**
     * Return headers
     */
    private setHeaders(): HttpHeaders {
        // getting the access token
        const tokenResponse = this.tokenService.getAccessToken();

        // value to return
        let headers;

        if (tokenResponse.expired) {
            // if the token is expired, return null
            headers = null;
        } else if (tokenResponse.accessToken) {
            // if the token was not expired and there was an access token returned, set the headers using the access token
            headers = new HttpHeaders().set('Authorization', 'Bearer ' + tokenResponse.accessToken);
        } else {
            // if there were no headers return a new set of HTTP headers without an access token
            headers = new HttpHeaders();
        }

        if (headers != null) {
            return headers.append('Content-Type', 'application/json');
        } else {
            return headers;
        }
    }
}
